import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PackagePlus, Plus, Trash2, UserPlus } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { CadastroRapidoProduto, CadastroRapidoServicoContratado, type CriadoRapido } from '../../componentes/cadastro-rapido';
import { CampoControlado, Entrada, Rotulo, useCamposPersonalizados } from '../../componentes/formulario';
import { anunciarOcultos } from '../../componentes/sensivel';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { useValidacaoLocal } from '../../componentes/validacao-local';
import { cabecalhoEmpresa, ErroRequisicao, get, getTodos, mensagemDeErro, post } from '../../nucleo/api';
import { precisaEscolherEmpresa } from '../../nucleo/empresa/empresa-do-registro';
import { data as formatarData, dinheiro, nomePessoa } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { FORMAS_PAGAMENTO, TIPOS_COMPRA, linhaDoTipo, parcelasDaCondicao, sugestaoDoContratado, totalDaLinha, valoresDasParcelas, vencimentosDaPrevia, type TipoCompra } from './compra-tipos';
import { NovoFornecedor } from '../../componentes/novo-fornecedor';
import { hojeLocal, invalidarCompras, numeroDe, type FornecedorDetalhe } from './tipos';
import { rotuloForma } from '../../nucleo/forma-pagamento';

interface LinhaCompra {
  chave: string;
  /** Fase 2 (W31, 5.2): serviço contratado de terceiros do Cadastros (opcional); a categoria de despesa dele vale quando o item não tem. */
  contratado: string;
  contratadoRotulo: string;
  /** Fase 2 (W25, K14): produto do Estoque (só com o Estoque instalado); lote 09/10 (C3): dá entrada no Estoque ao salvar. */
  produto: string;
  produtoRotulo: string;
  descricao: string;
  quantidade: string;
  valor: number | null;
  categoria: string;
  centro: string;
}

type Condicao = 'avista' | 'mensal' | 'prazos';

const novaLinha = (): LinhaCompra => ({ chave: crypto.randomUUID(), contratado: '', contratadoRotulo: '', produto: '', produtoRotulo: '', descricao: '', quantidade: '1', valor: null, categoria: '', centro: '' });
const inicioDoMes = (d: string) => `${d.slice(0, 7)}-01`;

/** Opções de categoria de despesa, centro de custo e conta financeira do Financeiro (só para quem lê o Financeiro). */
function useClassificacao(habilitado: boolean) {
  const categorias = useQuery({
    queryKey: ['/financeiro/categorias', 'despesa-ativas'],
    // Fase 2, W24 (K9): só as analíticas (a sintética só agrupa e o Contas recusa no item).
    queryFn: () => getTodos<{ id: string; nome: string; codigo: string | null }>('/financeiro/categorias', { tipo: 'despesa', ativa: true, lancavel: true }),
    enabled: habilitado,
    staleTime: 30_000,
  });
  const centros = useQuery({
    queryKey: ['/financeiro/centros-custo', 'ativos'],
    queryFn: () => getTodos<{ id: string; nome: string }>('/financeiro/centros-custo', { ativo: true }),
    enabled: habilitado,
    staleTime: 30_000,
  });
  // Lote 09/10 (C3): "Conta bancária" da Classificação financeira do antigo (vira a conta prevista nas contas a pagar).
  const contas = useQuery({
    queryKey: ['/financeiro/contas', 'ativas'],
    queryFn: () => getTodos<{ id: string; nome: string }>('/financeiro/contas', { ativa: true }),
    enabled: habilitado,
    staleTime: 30_000,
  });
  return {
    categorias: (categorias.data?.itens ?? []).map((c) => ({ valor: c.id, rotulo: c.codigo ? `${c.codigo} ${c.nome}` : c.nome })),
    centros: (centros.data?.itens ?? []).map((c) => ({ valor: c.id, rotulo: c.nome })),
    contas: (contas.data?.itens ?? []).map((c) => ({ valor: c.id, rotulo: c.nome })),
  };
}

/** Fase 2 (W8 + W6): formas de pagamento do catálogo do Cadastros ("Pix Sicredi"); vazio cai na lista fixa de tipos. */
function useFormasDoCatalogo(habilitado: boolean) {
  const q = useQuery({
    queryKey: ['/cadastros/formas-pagamento', 'ativas'],
    queryFn: () => getTodos<{ id: string; nome: string; tipo?: string; abreviatura?: string | null }>('/cadastros/formas-pagamento', { ativa: true }),
    enabled: habilitado,
    staleTime: 30_000,
  });
  return (q.data?.itens ?? []).map((f) => ({ valor: f.id, rotulo: rotuloForma(f) }));
}

/** Área do formulário (fieldset com legend), o mesmo desenho do bloco "Nota fiscal" de antes. */
function Area({ titulo, children, nome }: { titulo: string; children: ReactNode; nome: string }) {
  return (
    <fieldset data-area={nome} className="grid min-w-0 grid-cols-2 gap-3 rounded-lg border border-borda p-3 sm:col-span-6 sm:grid-cols-6">
      <legend className="px-1 text-sm font-medium text-tinta-2">{titulo}</legend>
      {children}
    </fieldset>
  );
}

/**
 * Fase 2 (W8, B §9.4 camada 1): nova compra direta em um passo. Lote 09/10 (C3): um formulário só, para todos (sem perfil
 * novo), em áreas na ordem e com os nomes do sistema antigo: Tipo da compra; Prestador de serviço ou Emitente; Nota fiscal;
 * Discriminação do serviço ou Produtos / itens (só "Serviço contratado" ou Produto no item, conforme o tipo); Valor ou
 * Valores e impostos; Datas; Condições de pagamento ou Duplicatas / parcelamento (prévia em tabela); Classificação
 * financeira (com a conta bancária prevista); Observações. O id nasce com a tela: reenviar não duplica.
 */
export function FormCompra({ aberto, aoFechar, aoSalvar }: { aberto: boolean; aoFechar: () => void; aoSalvar?: (id: string) => void }) {
  if (!aberto) return null;
  return <Corpo aoFechar={aoFechar} aoSalvar={aoSalvar} />;
}

function Corpo({ aoFechar, aoSalvar }: { aoFechar: () => void; aoSalvar?: (id: string) => void }) {
  const { t } = useTranslation('compras');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const s = useSessao();
  const moeda = s.workspace.moeda;
  const leFinanceiro = s.instalado('financeiro') && s.pode('financeiro.ver');
  const { categorias, centros, contas } = useClassificacao(leFinanceiro);
  const leCadastros = s.instalado('cadastros') && s.pode('cadastros.ver');
  const formasCatalogo = useFormasDoCatalogo(leCadastros);
  // Fase 2 (W25, K14): produto do Estoque no item e cadastro rápido de serviço contratado e de produto.
  const leEstoque = s.instalado('estoque') && s.pode('estoque.ver');
  const [rapidoItem, setRapidoItem] = useState<{ chave: string; tipo: 'servico' | 'produto' } | null>(null);
  const [minimo, setMinimo] = useState<number | null>(null);
  const [id] = useState(() => crypto.randomUUID());
  const hoje = hojeLocal();
  const [tipo, setTipo] = useState<TipoCompra>('servico');
  const [fornecedorId, setFornecedorId] = useState<string | null>(null);
  const [fornecedorRotulo, setFornecedorRotulo] = useState('');
  const [rapido, setRapido] = useState(false);
  const [descricao, setDescricao] = useState('');
  const [competencia, setCompetencia] = useState(inicioDoMes(hoje));
  const [nf, setNf] = useState({ numero: '', serie: '', chave: '', emissao: '' });
  const [detalhar, setDetalhar] = useState(false);
  const [total, setTotal] = useState<number | null>(null);
  const [categoria, setCategoria] = useState('');
  const [centro, setCentro] = useState('');
  const [conta, setConta] = useState('');
  const [linhas, setLinhas] = useState<LinhaCompra[]>([novaLinha()]);
  const [condicao, setCondicao] = useState<Condicao>('avista');
  const [parcelas, setParcelas] = useState('2');
  const [prazos, setPrazos] = useState('30/60');
  const [vencimento, setVencimento] = useState(hoje);
  const [forma, setForma] = useState('');
  const [observacoes, setObservacoes] = useState('');
  // Fase 2 (W19, K5): campos personalizados da compra direta (`compras.compra`), como no pedido.
  const extrasDefs = useCamposPersonalizados('compras.compra');
  const [extras, setExtras] = useState<Record<string, unknown>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [erros, setErros] = useState<Record<string, string>>({});
  const { ref } = useValidacaoLocal(erros, erro);
  const produto = tipo === 'produto';

  const muda = (chave: string, parcial: Partial<LinhaCompra>) => setLinhas((ls) => ls.map((l) => (l.chave === chave ? { ...l, ...parcial } : l)));
  const totalLinha = (l: LinhaCompra) => totalDaLinha(numeroDe(l.quantidade) ?? 0, l.valor ?? 0);
  const totalCalculado = detalhar ? linhas.reduce((a, l) => a + totalLinha(l), 0) : (total ?? 0);
  const n = parcelasDaCondicao(condicao, parcelas, prazos);
  const previa = useMemo(() => valoresDasParcelas(totalCalculado, n), [totalCalculado, n]);
  const vencimentos = useMemo(() => vencimentosDaPrevia(condicao, parcelas, prazos, vencimento), [condicao, parcelas, prazos, vencimento]);
  const empresaAtual = s.empresas?.opcoes.find((e) => e.id === s.empresas?.atual)?.nome ?? null;
  // V2 verticais: sem empresa na aba (sem filtro, várias permitidas), a compra pede a empresa.
  const escolherEmpresa = precisaEscolherEmpresa(s.empresas);
  const [empresaRegistro, setEmpresaRegistro] = useState('');

  function trocarTipo(novo: TipoCompra) {
    setTipo(novo);
    setLinhas((ls) => ls.map((l) => linhaDoTipo(l, novo)));
  }

  async function escolherFornecedor(novo: string | null) {
    setFornecedorId(novo);
    if (!novo) return;
    try {
      // Condições padrão do fornecedor (Fase 2, W25: moram na ficha do Cadastros; Compras > Fornecedores edita o mesmo dado).
      const f = await get<FornecedorDetalhe>(`/compras/fornecedores/${novo}`);
      const c = f.condicao_pagamento?.trim();
      if (c && c !== 'avista') {
        setCondicao('prazos');
        setPrazos(c);
      } else if (c === 'avista') setCondicao('avista');
      if (f.forma_pagamento_id && formasCatalogo.some((x) => x.valor === f.forma_pagamento_id)) setForma(f.forma_pagamento_id);
      setMinimo(f.pedido_minimo_centavos ?? null);
    } catch {
      // Sem condição cadastrada ou sem permissão: fica o que está na tela.
    }
  }

  /**
   * Lote 09/10 (B2): o serviço contratado escolhido sugere, no que estiver vazio, o valor unitário (valor de referência,
   * como no antigo), a descrição (o nome) e a categoria de despesa. Dá para trocar tudo no item.
   */
  async function escolherContratado(chave: string, novo: string) {
    muda(chave, { contratado: novo });
    if (!novo) return;
    try {
      const sc = await get<{ nome: string; valor_referencia_centavos: number | null; categoria_despesa_id: string | null }>(`/cadastros/servicos-contratados/${novo}`);
      setLinhas((ls) => ls.map((l) => (l.chave !== chave || l.contratado !== novo ? l : sugestaoDoContratado(l, sc, categorias.map((c) => c.valor)))));
    } catch {
      // Sem permissão de leitura ou serviço sumido: fica o que está na tela (o servidor confere na gravação).
    }
  }

  const salvar = useMutation({
    mutationFn: async () => {
      if (escolherEmpresa && !empresaRegistro) throw new Error(tn('empresa.escolha'));
      if (!fornecedorId) throw new Error(t('compra.erros.fornecedor'));
      if (!descricao.trim()) throw new Error(t('compra.erros.descricao'));
      const temNf = nf.numero.trim() || nf.serie.trim() || nf.chave.trim() || nf.emissao;
      const corpo: Record<string, unknown> = {
        id,
        tipo,
        fornecedor_id: fornecedorId,
        descricao: descricao.trim(),
        nf: temNf ? { numero: nf.numero.trim() || null, serie: nf.serie.trim() || null, chave: nf.chave.replace(/\s+/g, '') || null, emissao: nf.emissao || null } : null,
        competencia,
        primeiro_vencimento: vencimento,
        ...(formasCatalogo.length ? { forma_pagamento_id: forma || null } : { forma_pagamento: forma || null }),
        ...(leFinanceiro ? { conta_prevista_id: conta || null } : {}),
        observacoes: observacoes.trim() || null,
        extras,
        ...(condicao === 'mensal' ? { parcelas: Number(parcelas) } : { condicao_pagamento: condicao === 'avista' ? 'avista' : prazos.trim() }),
      };
      if (detalhar) {
        const validas = linhas.filter((l) => l.descricao.trim() || l.valor);
        if (!validas.length || validas.some((l) => !l.descricao.trim() || !numeroDe(l.quantidade) || l.valor === null)) throw new Error(t('compra.erros.item'));
        corpo.itens = validas.map((l) => ({
          servico_contratado_id: produto ? null : l.contratado || null,
          produto_id: produto ? l.produto || null : null,
          descricao: l.descricao.trim(),
          quantidade: numeroDe(l.quantidade),
          valor_unitario_centavos: l.valor,
          categoria_id: l.categoria || null,
          centro_custo_id: l.centro || null,
        }));
      } else {
        if (!total) throw new Error(t('compra.erros.valor'));
        Object.assign(corpo, { total_centavos: total, categoria_id: categoria || null, centro_custo_id: centro || null });
      }
      const r = await post<{ documentos: number; entradas_estoque?: number }>('/compras/compras', corpo, { 'Idempotency-Key': id, ...cabecalhoEmpresa(empresaRegistro) });
      return { id, entradas: r?.entradas_estoque ?? 0 };
    },
    onSuccess: async ({ id: novo, entradas }) => {
      await invalidarCompras(qc);
      toast.sucesso(entradas ? t('c3.salvaEstoque') : t('compra.salva'));
      aoSalvar?.(novo);
      aoFechar();
    },
    onError: (e) => {
      setErros(e instanceof ErroRequisicao ? e.porCampo() : {});
      setErro(mensagemDeErro(e));
    },
  });

  function submeter(ev: FormEvent) {
    ev.preventDefault();
    setErro(null);
    setErros({});
    salvar.mutate();
  }

  /** O cadastrado na hora entra no item: escolhido, com a descrição (se vazia) e o valor de referência ou custo (se vazio). */
  function criadoNoItem(tipoItem: 'servico' | 'produto', c: CriadoRapido) {
    const chave = rapidoItem?.chave;
    if (!chave) return;
    setLinhas((ls) =>
      ls.map((l) =>
        l.chave !== chave
          ? l
          : {
              ...l,
              ...(tipoItem === 'servico' ? { contratado: c.id, contratadoRotulo: c.nome } : { produto: c.id, produtoRotulo: c.nome }),
              descricao: l.descricao.trim() ? l.descricao : c.nome,
              valor: l.valor ?? (c.custo_centavos > 0 ? c.custo_centavos : null),
            },
      ),
    );
    toast.sucesso(tipoItem === 'servico' ? t('c3.contratadoCriado') : t('w25.produtoCriado'));
  }

  const opcoesCondicao = (['avista', 'mensal', 'prazos'] as const).map((c) => ({ valor: c, rotulo: t(`compra.condicoes.${c}`) }));

  return (
    <>
      <Drawer
        aberto
        aoFechar={aoFechar}
        largura="lg"
        titulo={t('compra.nova')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={aoFechar}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-compra" carregando={salvar.isPending}>
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        <form ref={ref} id="form-compra" onSubmit={submeter} noValidate className="grid grid-cols-1 gap-4 sm:grid-cols-6">
          {erro && (
            <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo sm:col-span-6">
              {erro}
            </p>
          )}

          {/* 1. Tipo da compra */}
          <fieldset data-area="tipo" className="min-w-0 rounded-lg border border-borda p-3 sm:col-span-6">
            <legend className="px-1 text-sm font-medium text-tinta-2">{t('c3.tipo')}</legend>
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-6">
              {TIPOS_COMPRA.map((x) => (
                <label key={x} className="flex cursor-pointer items-center gap-2 text-sm text-tinta-1">
                  <input type="radio" name="tipo-compra" value={x} checked={tipo === x} onChange={() => trocarTipo(x)} className="size-4 accent-acento" />
                  {t(`c3.tipos.${x}`)}
                </label>
              ))}
            </div>
          </fieldset>

          {/* 2. Prestador de serviço | Emitente (fornecedor) */}
          <Area nome="fornecedor" titulo={produto ? t('c3.emitente') : t('c3.prestador')}>
            {escolherEmpresa ? (
              <div className="col-span-2 min-w-0 sm:col-span-6">
                <CampoControlado
                  def={{ nome: 'empresa', rotulo: t('c3.empresa'), tipo: 'selecao', obrigatorio: true, ajuda: tn('empresa.ajudaRegistro'), opcoes: (s.empresas?.opcoes ?? []).map((e) => ({ valor: e.id, rotulo: e.nome })) }}
                  valor={empresaRegistro}
                  aoMudar={(v) => setEmpresaRegistro(String(v ?? ''))}
                />
              </div>
            ) : (
              s.empresas &&
              empresaAtual && (
                <div className="col-span-2 flex min-w-0 flex-col gap-1 sm:col-span-6">
                  <span className="text-sm font-medium text-tinta-2">{t('c3.empresa')}</span>
                  <span className="text-sm text-tinta-1">{empresaAtual}</span>
                  <span className="text-xs text-tinta-3">{t('c3.empresaAjuda')}</span>
                </div>
              )
            )}
            <div className="col-span-2 flex min-w-0 items-end gap-2 sm:col-span-4">
              <div className="min-w-0 flex-1">
                <CampoControlado
                  def={{ nome: 'fornecedor_id', rotulo: t('compra.fornecedor'), tipo: 'busca', obrigatorio: true, busca: { caminho: '/cadastros/parceiros', params: { papel: 'fornecedor' }, rotulo: (i) => String(i.nome) } }}
                  valor={fornecedorId}
                  rotuloAtual={fornecedorRotulo}
                  aoMudar={(v) => void escolherFornecedor((v as string | null) || null)}
                  erro={erros.fornecedor_id}
                />
              </div>
              {s.pode('cadastros.criar') && (
                <Botao className="mb-px shrink-0" icone={<UserPlus className="size-4" />} onClick={() => setRapido(true)}>
                  <span className="hidden sm:inline">{t('compra.novoFornecedor')}</span>
                  <span className="sr-only sm:hidden">{t('compra.novoFornecedor')}</span>
                </Botao>
              )}
            </div>
            <div className="col-span-2 flex min-w-0 flex-col gap-1 sm:col-span-2">
              <span className="text-sm font-medium text-tinta-2">{t('c3.numeroCompra')}</span>
              <span className="flex min-h-10 items-center text-sm text-tinta-3">{t('c3.numeroGerado')}</span>
            </div>
          </Area>

          {/* 3. Nota fiscal de serviço (NFS-e) | Nota fiscal de produto (NF-e) */}
          <Area nome="nf" titulo={produto ? t('c3.nfProduto') : t('c3.nfServico')}>
            <div className="col-span-1 sm:col-span-2">
              <CampoControlado def={{ nome: 'nf.numero', rotulo: t('c3.nfNumero'), tipo: 'texto' }} valor={nf.numero} aoMudar={(v) => setNf((x) => ({ ...x, numero: String(v ?? '') }))} erro={erros['nf.numero']} />
            </div>
            <div className="col-span-1 sm:col-span-1">
              <CampoControlado def={{ nome: 'nf.serie', rotulo: t('compra.nfSerie'), tipo: 'texto' }} valor={nf.serie} aoMudar={(v) => setNf((x) => ({ ...x, serie: String(v ?? '') }))} erro={erros['nf.serie']} />
            </div>
            <div className="col-span-2 sm:col-span-3">
              <CampoControlado def={{ nome: 'nf.emissao', rotulo: t('c3.nfEmissao'), tipo: 'data' }} valor={nf.emissao} aoMudar={(v) => setNf((x) => ({ ...x, emissao: String(v ?? '') }))} erro={erros['nf.emissao']} />
            </div>
            <div className="col-span-2 sm:col-span-6">
              <CampoControlado def={{ nome: 'nf.chave', rotulo: t('compra.nfChave'), tipo: 'texto' }} valor={nf.chave} aoMudar={(v) => setNf((x) => ({ ...x, chave: String(v ?? '') }))} erro={erros['nf.chave']} />
            </div>
          </Area>

          {/* 4. Discriminação do serviço | Produtos / itens */}
          <Area nome="itens" titulo={produto ? t('c3.produtosItens') : t('c3.discriminacaoServico')}>
            <div className="col-span-2 sm:col-span-6">
              <CampoControlado
                def={{ nome: 'descricao', rotulo: produto ? t('c3.descricaoProduto') : t('c3.descricaoServico'), tipo: 'texto', obrigatorio: true }}
                valor={descricao}
                aoMudar={(v) => setDescricao(String(v ?? ''))}
                erro={erros.descricao}
              />
            </div>
            <div className="col-span-2 sm:col-span-6">
              <CampoControlado def={{ nome: 'detalhar', rotulo: t('compra.detalharItens'), tipo: 'booleano' }} valor={detalhar} aoMudar={(v) => setDetalhar(!!v)} />
              <p className="mt-1 text-xs text-tinta-3">{t('compra.detalharItensAjuda')}</p>
            </div>
            {detalhar && (
              <div className="col-span-2 flex min-w-0 flex-col gap-3 sm:col-span-6">
                {linhas.map((l, i) => (
                  <div key={l.chave} className="grid min-w-0 grid-cols-6 gap-2 rounded-lg border border-borda bg-sup-1 p-3">
                    {!produto && leCadastros && (
                      // Lote 09/10 (C3): só o "Serviço contratado" (o "Serviço tomado" do antigo).
                      <div className="col-span-6 flex min-w-0 items-end gap-2">
                        <div className="min-w-0 flex-1">
                          <CampoControlado
                            def={{ nome: `item-contratado-${l.chave}`, rotulo: t('c3.servicoContratado'), tipo: 'busca', busca: { caminho: '/cadastros/servicos-contratados', params: { ativo: true }, rotulo: (x) => String(x.nome) } }}
                            valor={l.contratado || null}
                            rotuloAtual={l.contratadoRotulo}
                            aoMudar={(v) => void escolherContratado(l.chave, (v as string | null) ?? '')}
                            erro={erros['itens.servico_contratado_id']}
                          />
                        </div>
                        {s.pode('cadastros.criar') && (
                          <Botao className="mb-px shrink-0" data-novo-servico="" icone={<Plus className="size-4" />} onClick={() => setRapidoItem({ chave: l.chave, tipo: 'servico' })}>
                            <span className="hidden sm:inline">{t('c3.novoServicoContratado')}</span>
                            <span className="sr-only sm:hidden">{t('c3.novoServicoContratado')}</span>
                          </Botao>
                        )}
                      </div>
                    )}
                    {produto && leEstoque && (
                      <div className="col-span-6 flex min-w-0 items-end gap-2">
                        <div className="min-w-0 flex-1">
                          <CampoControlado
                            def={{ nome: `item-produto-${l.chave}`, rotulo: t('w25.produto'), tipo: 'busca', busca: { caminho: '/estoque/produtos', params: { ativo: true }, rotulo: (x) => `${String(x.codigo)} ${String(x.nome)}` } }}
                            valor={l.produto || null}
                            rotuloAtual={l.produtoRotulo}
                            aoMudar={(v) => muda(l.chave, { produto: (v as string | null) ?? '' })}
                            erro={erros['itens.produto_id']}
                          />
                        </div>
                        {s.pode('estoque.criar') && (
                          <Botao className="mb-px shrink-0" data-novo-produto="" icone={<PackagePlus className="size-4" />} onClick={() => setRapidoItem({ chave: l.chave, tipo: 'produto' })}>
                            <span className="hidden sm:inline">{t('w25.novoProduto')}</span>
                            <span className="sr-only sm:hidden">{t('w25.novoProduto')}</span>
                          </Botao>
                        )}
                      </div>
                    )}
                    <div className="col-span-6">
                      <CampoControlado def={{ nome: `item-desc-${l.chave}`, rotulo: `${t('compra.itemDescricao')} ${i + 1}`, tipo: 'texto' }} valor={l.descricao} aoMudar={(v) => muda(l.chave, { descricao: String(v ?? '') })} />
                    </div>
                    <div className="col-span-2">
                      <Rotulo id={`item-qtd-${l.chave}`} rotulo={t('c3.qtd')}>
                        <Entrada id={`item-qtd-${l.chave}`} inputMode="decimal" className="numero text-right" value={l.quantidade} onChange={(e) => muda(l.chave, { quantidade: e.target.value })} />
                      </Rotulo>
                    </div>
                    <div className="col-span-4 sm:col-span-2">
                      <CampoControlado def={{ nome: `item-valor-${l.chave}`, rotulo: t('c3.vlrUnit'), tipo: 'dinheiro' }} valor={l.valor} aoMudar={(v) => muda(l.chave, { valor: (v as number | null) ?? null })} />
                    </div>
                    <div className="col-span-5 flex min-w-0 flex-col justify-end gap-1 sm:col-span-1">
                      <span className="text-sm font-medium text-tinta-2">{t('c3.previa.total')}</span>
                      <span className="numero flex min-h-10 items-center text-sm text-tinta-1">{anunciarOcultos(dinheiro(totalLinha(l), moeda))}</span>
                    </div>
                    <div className="col-span-1 flex items-end justify-end">
                      <Botao variante="fantasma" aria-label={t('compra.removerItem')} title={t('compra.removerItem')} disabled={linhas.length === 1} onClick={() => setLinhas((ls) => ls.filter((x) => x.chave !== l.chave))} icone={<Trash2 className="size-4" />} />
                    </div>
                    {leFinanceiro && (
                      <>
                        <div className="col-span-6 sm:col-span-3">
                          <CampoControlado def={{ nome: `item-cc-${l.chave}`, rotulo: t('compra.centroCusto'), tipo: 'selecao', opcoes: centros }} valor={l.centro} aoMudar={(v) => muda(l.chave, { centro: String(v ?? '') })} />
                        </div>
                        <div className="col-span-6 sm:col-span-3">
                          <CampoControlado def={{ nome: `item-cat-${l.chave}`, rotulo: t('c3.planoContas'), tipo: 'selecao', opcoes: categorias }} valor={l.categoria} aoMudar={(v) => muda(l.chave, { categoria: String(v ?? '') })} />
                        </div>
                      </>
                    )}
                  </div>
                ))}
                {!leFinanceiro && <p className="text-xs text-tinta-3">{t('compra.semFinanceiro')}</p>}
                {(erros['itens.categoria_id'] || erros['itens.centro_custo_id']) && (
                  <p role="alert" className="text-sm text-perigo">
                    {erros['itens.categoria_id'] ?? erros['itens.centro_custo_id']}
                  </p>
                )}
                {produto && leEstoque && <p className="text-xs text-tinta-3">{t('c3.entradaEstoque')}</p>}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Botao icone={<Plus className="size-4" />} onClick={() => setLinhas((ls) => [...ls, novaLinha()])}>
                    {t('compra.adicionarItem')}
                  </Botao>
                  <p className="numero text-sm text-tinta-2">
                    {t('compra.somaItens')}: <span className="font-semibold text-tinta-1">{anunciarOcultos(dinheiro(totalCalculado, moeda))}</span>
                  </p>
                </div>
              </div>
            )}
          </Area>

          {/* 5. Valor | Valores e impostos */}
          <Area nome="valor" titulo={produto ? t('c3.valoresImpostos') : t('c3.valor')}>
            {detalhar ? (
              <div className="col-span-2 flex min-w-0 flex-col gap-1 sm:col-span-3">
                <span className="text-sm font-medium text-tinta-2">{t('compra.valorTotal')}</span>
                <span className="numero flex min-h-10 items-center text-sm text-tinta-1">{anunciarOcultos(dinheiro(totalCalculado, moeda))}</span>
              </div>
            ) : (
              <div className="col-span-2 sm:col-span-3">
                <CampoControlado def={{ nome: 'total_centavos', rotulo: t('compra.valorTotal'), tipo: 'dinheiro', obrigatorio: true }} valor={total} aoMudar={(v) => setTotal((v as number | null) ?? null)} erro={erros.total_centavos} />
              </div>
            )}
            {produto && <p className="col-span-2 text-xs text-tinta-3 sm:col-span-3 sm:self-end">{t('c3.impostosAviso')}</p>}
          </Area>

          {/* 6. Datas */}
          <Area nome="datas" titulo={t('c3.datas')}>
            <div className="col-span-2 sm:col-span-3">
              <CampoControlado def={{ nome: 'competencia', rotulo: t('compra.competencia'), tipo: 'data', obrigatorio: true }} valor={competencia} aoMudar={(v) => setCompetencia(String(v ?? ''))} erro={erros.competencia} />
            </div>
            <div className="col-span-2 sm:col-span-3">
              <CampoControlado def={{ nome: 'primeiro_vencimento', rotulo: t('c3.primeiroVencimento'), tipo: 'data', obrigatorio: true }} valor={vencimento} aoMudar={(v) => setVencimento(String(v ?? ''))} erro={erros.primeiro_vencimento} />
            </div>
          </Area>

          {/* 7. Condições de pagamento | Duplicatas / parcelamento */}
          <Area nome="pagamento" titulo={produto ? t('c3.duplicatas') : t('c3.condicoesPagamento')}>
            <div className="col-span-2 sm:col-span-2">
              <CampoControlado
                def={{ nome: 'forma_pagamento', rotulo: t('compra.forma'), tipo: 'selecao', opcoes: formasCatalogo.length ? formasCatalogo : FORMAS_PAGAMENTO.map((f) => ({ valor: f, rotulo: t(`compra.formas.${f}`) })) }}
                valor={forma}
                aoMudar={(v) => setForma(String(v ?? ''))}
                erro={erros.forma_pagamento ?? erros.forma_pagamento_id}
              />
            </div>
            <div className="col-span-2 sm:col-span-2">
              <CampoControlado def={{ nome: 'condicao', rotulo: t('compra.condicao'), tipo: 'selecao', obrigatorio: true, opcoes: opcoesCondicao }} valor={condicao} aoMudar={(v) => setCondicao((v as Condicao) || 'avista')} />
            </div>
            {condicao === 'mensal' && (
              <div className="col-span-2 sm:col-span-2">
                <Rotulo id="compra-parcelas" rotulo={t('c3.numeroParcelas')}>
                  <Entrada id="compra-parcelas" inputMode="numeric" className="numero text-right" value={parcelas} aria-invalid={!!erros.parcelas} onChange={(e) => setParcelas(e.target.value.replace(/\D/g, '').slice(0, 3))} />
                </Rotulo>
              </div>
            )}
            {condicao === 'prazos' && (
              <div className="col-span-2 sm:col-span-2">
                <CampoControlado def={{ nome: 'condicao_pagamento', rotulo: t('compra.prazos'), tipo: 'texto', placeholder: '30/60/90' }} valor={prazos} aoMudar={(v) => setPrazos(String(v ?? ''))} erro={erros.condicao_pagamento} />
              </div>
            )}
            {condicao === 'prazos' && <p className="col-span-2 -mt-1 text-xs text-tinta-3 sm:col-span-6">{t('compra.prazosAjuda')}</p>}
            <div aria-live="polite" className="col-span-2 sm:col-span-6">
              {previa.length ? (
                <div className="overflow-x-auto rounded-lg border border-borda">
                  <table data-previa="" className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-borda text-left text-tinta-3">
                        <th scope="col" className="px-3 py-2 font-medium">
                          {t('c3.previa.parcela')}
                        </th>
                        <th scope="col" className="px-3 py-2 font-medium">
                          {t('c3.previa.vencimento')}
                        </th>
                        <th scope="col" className="px-3 py-2 text-right font-medium">
                          {t('c3.previa.valor')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {previa.map((v, k) => (
                        <tr key={k} className="border-b border-borda last:border-0">
                          <td className="numero px-3 py-2 text-tinta-2">
                            {k + 1}/{previa.length}
                          </td>
                          <td className="numero px-3 py-2 text-tinta-1">{vencimentos[k] ? formatarData(vencimentos[k]) : ''}</td>
                          <td className="numero px-3 py-2 text-right text-tinta-1">{anunciarOcultos(dinheiro(v, moeda))}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-borda">
                        <th scope="row" colSpan={2} className="px-3 py-2 text-left font-medium text-tinta-2">
                          {t('c3.previa.total')}
                        </th>
                        <td className="numero px-3 py-2 text-right font-semibold text-tinta-1">{anunciarOcultos(dinheiro(totalCalculado, moeda))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="numero rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm text-tinta-2">{t('compra.previaVazia')}</p>
              )}
            </div>
            {minimo !== null && minimo > 0 && (
              <p data-pedido-minimo="" role={totalCalculado > 0 && totalCalculado < minimo ? 'alert' : undefined} className={`col-span-2 text-sm sm:col-span-6 ${totalCalculado > 0 && totalCalculado < minimo ? 'text-perigo' : 'text-tinta-3'}`}>
                {anunciarOcultos(totalCalculado > 0 && totalCalculado < minimo ? t('w25.minimoAviso', { valor: dinheiro(minimo, moeda) }) : t('w25.minimoInfo', { valor: dinheiro(minimo, moeda) }))}
              </p>
            )}
          </Area>

          {/* 8. Classificação financeira */}
          <Area nome="classificacao" titulo={t('c3.classificacao')}>
            {leFinanceiro ? (
              <>
                {!detalhar && (
                  <>
                    <div className="col-span-2 sm:col-span-2">
                      <CampoControlado def={{ nome: 'categoria_id', rotulo: t('c3.planoContas'), tipo: 'selecao', opcoes: categorias }} valor={categoria} aoMudar={(v) => setCategoria(String(v ?? ''))} erro={erros.categoria_id ?? erros['itens.categoria_id']} />
                    </div>
                    <div className="col-span-2 sm:col-span-2">
                      <CampoControlado def={{ nome: 'centro_custo_id', rotulo: t('compra.centroCusto'), tipo: 'selecao', opcoes: centros }} valor={centro} aoMudar={(v) => setCentro(String(v ?? ''))} erro={erros.centro_custo_id ?? erros['itens.centro_custo_id']} />
                    </div>
                  </>
                )}
                <div className="col-span-2 sm:col-span-2">
                  <CampoControlado def={{ nome: 'conta_prevista_id', rotulo: t('c3.contaBancaria'), tipo: 'selecao', opcoes: contas, ajuda: t('c3.contaBancariaAjuda') }} valor={conta} aoMudar={(v) => setConta(String(v ?? ''))} erro={erros.conta_prevista_id} />
                </div>
              </>
            ) : (
              <p className="col-span-2 text-xs text-tinta-3 sm:col-span-6">{t('compra.semFinanceiro')}</p>
            )}
          </Area>

          {/* 9. Observações */}
          <Area nome="observacoes" titulo={t('compra.observacoes')}>
            <div className="col-span-2 sm:col-span-6">
              <CampoControlado def={{ nome: 'observacoes', rotulo: t('compra.observacoes'), tipo: 'area' }} valor={observacoes} aoMudar={(v) => setObservacoes(String(v ?? ''))} erro={erros.observacoes} />
            </div>
            {extrasDefs.map((c) => (
              <div key={c.nome} data-campo={c.nome} className="col-span-2 sm:col-span-3">
                <CampoControlado def={c} valor={extras[c.nome.slice(7)]} aoMudar={(v) => setExtras((x) => ({ ...x, [c.nome.slice(7)]: v }))} erro={erros[c.nome] ?? erros[c.nome.slice(7)]} />
              </div>
            ))}
          </Area>
        </form>
      </Drawer>
      <CadastroRapidoServicoContratado
        aberto={rapidoItem?.tipo === 'servico'}
        aoFechar={() => setRapidoItem(null)}
        titulo={t('c3.novoServicoContratado')}
        aoCriar={(c) => criadoNoItem('servico', c)}
      />
      <CadastroRapidoProduto
        aberto={rapidoItem?.tipo === 'produto'}
        aoFechar={() => setRapidoItem(null)}
        titulo={t('w25.novoProduto')}
        aoCriar={(c) => criadoNoItem('produto', c)}
      />
      <NovoFornecedor
        aberto={rapido}
        aoFechar={() => setRapido(false)}
        aoCriar={(f) => {
          setFornecedorRotulo(nomePessoa(f.nome));
          void escolherFornecedor(f.id);
        }}
      />
    </>
  );
}
